import type { RecurringRevenueReport } from '@vy/types';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import {
  applyReportFilters,
  type FilterPredicateMap,
} from '../../reports-components/apply-report-filters';
import {
  asStringArray,
  matchesDatePreset,
  matchesLabelSelection,
  matchesStringArrayIntersection,
  matchesStringSelection,
} from '../../reports-components/shared-filter-fields/filter-matchers';
import { collectRowProducts } from '../../reports-components/shared-filter-fields/recurring-revenue-row-selectors';
import { RECURRING_REVENUE_FILTER_FIELD_IDS } from '../hooks/recurring-revenue-filter-field-ids';

type Row = RecurringRevenueReport[number];

const PREDICATES: FilterPredicateMap<Row> = {
  [RECURRING_REVENUE_FILTER_FIELD_IDS.CustomerId]: (row, value) =>
    matchesStringSelection((row as { customerId?: string }).customerId, value),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Subsidiary]: (row, value) =>
    matchesStringSelection(row.subsidiaryName ?? null, value),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Country]: (row, value) =>
    matchesStringSelection((row as { country?: string | null }).country ?? null, value),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.State]: (row, value) =>
    matchesStringSelection((row as { state?: string | null }).state ?? null, value),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Product]: (row, value) =>
    matchesStringArrayIntersection(
      collectRowProducts(row).map((product) => product.productId),
      value,
    ),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.ContractStatus]: (row, value) =>
    asStringArray(value).length === 0 || asStringArray(value).includes(row.contractStatus),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Currency]: (row, value) =>
    asStringArray(value).length === 0 || asStringArray(value).includes(row.originalCurrency),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Label]: (row, value) =>
    matchesLabelSelection(
      (row as { labels?: { id: string }[] }).labels?.map((label) => label.id) ?? [],
      value,
    ),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.Plan]: (row, value) =>
    matchesStringSelection(row.contractName ?? null, value),

  [RECURRING_REVENUE_FILTER_FIELD_IDS.ContractStartDate]: (row, value) =>
    matchesDatePreset(row.contractStartDate, value),
};

export function applyRecurringRevenueFilters(
  rows: RecurringRevenueReport,
  filters: FilterLineType[],
): RecurringRevenueReport {
  return applyReportFilters(rows, filters, PREDICATES);
}
